import {
  ChangeEvent,
  Fragment,
  KeyboardEvent,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  WithStyles,
  createStyles,
  withStyles,
} from '@material-ui/core';

import classNames from 'classnames';
import caret from 'textarea-caret';

import {
  InputGroup,
  type InputGroupTextareaProps,
  useCombinedRefs,
} from '@spotify/confidence-core-react';

import { useForceUpdate } from '../../hooks';
import { getActiveToken, replaceAt } from '../../utils';

const styles = createStyles({
  root: {},
  paper: {},
});

const POPVER_Y_OFFSET = 20;
const POPVER_X_OFFSET = 10;

export type UserSuggestion = {
  avatarUri: string | undefined;
  name: string;
  email: string;
};

interface OriginTransform {
  top: number;
  left: number;
}

export interface TextareaWithMentionsProps
  extends Omit<InputGroupTextareaProps, 'classes'>,
    WithStyles<typeof styles> {
  value: string;
  onValueChange: (text: string) => void;
  onSubmit?: () => void;
  onCancel?: () => void;
  autoFocus?: boolean;
  suggestions?: UserSuggestion[];
  minRows?: number;
  maxRows?: number;
  originTransform?: OriginTransform;
  onMentionSearch?: (query: string | undefined) => void;
}
// The maximum number of suggestions to show.
const MAX_SUGGESTIONS = 5;

// The character that triggers the suggestion box.
const COMPLETE_CHARACTER = '@';

const TextareaWithMentions = forwardRef<
  HTMLTextAreaElement,
  TextareaWithMentionsProps
>(
  (
    {
      classes,
      value,
      onValueChange,
      autoFocus = false,
      onSubmit,
      onCancel,
      disabled,
      placeholder,
      className,
      suggestions = [],
      minRows,
      maxRows,
      originTransform,
      onMentionSearch,
    },
    ref,
  ) => {
    const completeRef = useRef<HTMLTextAreaElement>(null);
    const combinedRef = useCombinedRefs<HTMLTextAreaElement>(ref, completeRef);

    // Currently selected suggestion, or null if none.
    const [selected, setSelected] = useState<UserSuggestion | null>(null);

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
      onValueChange(e.target.value);
    };

    const activeToken = getActiveToken(
      value,
      completeRef?.current?.selectionEnd ?? 0,
    );

    // Whether the suggestion box should be open.
    const open = Boolean(
      activeToken &&
        activeToken.word.startsWith(COMPLETE_CHARACTER) &&
        suggestions.length > 0,
    );

    // The index of the token to open the suggestion box at
    const openAt = activeToken?.range?.[0] ?? null;

    // The position of the suggestion box, used if offsetPosition is true
    const [position, setPosition] = useState<{
      top: number;
      left: number;
    } | null>(null);

    useLayoutEffect(() => {
      if (open) {
        if (openAt === null) return;
        if (!completeRef.current) return;

        const { left, top } = caret(completeRef.current, openAt);
        const textareaRect = completeRef.current.getBoundingClientRect();

        setPosition({
          top:
            textareaRect.top + top + (originTransform?.top ?? POPVER_Y_OFFSET),
          left:
            textareaRect.left +
            left +
            (originTransform?.left ?? POPVER_X_OFFSET),
        });
      }
    }, [open, openAt]);

    // The email we're possibly selecting.
    const possiblyEmail =
      activeToken?.word?.slice(COMPLETE_CHARACTER.length) ?? '';

    const isMentioning =
      activeToken?.word?.startsWith(COMPLETE_CHARACTER) ?? false;

    useEffect(() => {
      onMentionSearch?.(isMentioning ? possiblyEmail : undefined);
    }, [isMentioning, possiblyEmail]);

    const handleSelectSuggestion = (item: UserSuggestion) => {
      const [index] = activeToken?.range ?? [0, 0];
      const replacement = `@${item.email} `;
      const newValue = replaceAt(
        value,
        replacement,
        index,
        activeToken?.word.length ?? 0,
      );

      onValueChange(newValue);

      // Set new cursor position after the inserted text.  This will also
      // trigger a "select start" event, which will call onInputNavigate
      // that will close the suggestion box.
      if (completeRef.current) {
        completeRef.current?.setSelectionRange(
          index + replacement.length,
          index + replacement.length,
        );
        completeRef.current?.focus();
      }
    };

    const forceUpdate = useForceUpdate();

    const onInputNavigate = () => {
      const cursorPosition = completeRef.current?.selectionEnd || 0;
      const newToken = getActiveToken(value, cursorPosition);
      const shouldOpen =
        newToken && newToken?.word.startsWith(COMPLETE_CHARACTER);
      if (shouldOpen !== open) {
        // Navigation caused the state of "open" to change, so we need to
        // force an update to re-render the suggestion box.
        forceUpdate();
      }
    };

    const filteredSuggestions = suggestions.filter(
      option =>
        option.email.includes(possiblyEmail ?? '') ||
        option.name.includes(possiblyEmail ?? ''),
    );

    // When the suggestions change, select the first one.
    useEffect(() => {
      setSelected(
        filteredSuggestions.length > 0 ? filteredSuggestions[0] : null,
      );
    }, [JSON.stringify(filteredSuggestions.map(s => s.email))]);

    const handleKeyDown = (
      e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      if (e.key === 'Escape') {
        onCancel?.();
      }

      if (open) {
        const selectedIndex = filteredSuggestions.findIndex(
          suggestion => suggestion.email === selected?.email,
        );

        if (e.key === 'ArrowDown') {
          e.preventDefault();

          const newIndex = selectedIndex + 1;
          if (newIndex < filteredSuggestions.length) {
            setSelected(filteredSuggestions[newIndex]);
          }
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();

          const newIndex = selectedIndex - 1;
          if (newIndex >= 0) {
            setSelected(filteredSuggestions[newIndex]);
          }
        } else if (e.key === 'Enter') {
          e.preventDefault();
          if (selectedIndex !== -1 && selected) {
            handleSelectSuggestion(selected);
            return;
          }
        }
      }

      if (e.key === 'Enter') {
        if (!e.shiftKey && value?.trim().length > 0) {
          e.preventDefault();
          onSubmit?.();
        }
      }
    };

    return (
      <Fragment>
        <InputGroup.Textarea
          ref={combinedRef as React.RefObject<HTMLTextAreaElement>}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onClick={onInputNavigate}
          onSelect={onInputNavigate}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          placeholder={placeholder}
          disabled={disabled}
          className={classNames(classes.root, className)}
          minRows={minRows}
          maxRows={maxRows}
        />
        {open && (
          <Paper
            style={{
              overflow: 'clip',
              position: 'fixed',
              top: position?.top,
              left: position?.left,
              zIndex: 1300,
            }}
            className={classes.paper}
          >
            <List dense>
              {filteredSuggestions.slice(0, MAX_SUGGESTIONS).map(suggestion => (
                <ListItem
                  key={suggestion.email}
                  button
                  selected={suggestion.email === selected?.email}
                  onMouseDown={e => {
                    e.preventDefault(); // Prevent blur
                    handleSelectSuggestion(suggestion);
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={suggestion.avatarUri} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={suggestion.name}
                    secondary={suggestion.email}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Fragment>
    );
  },
);

export default withStyles(styles)(TextareaWithMentions);
